import { OpenOrders } from "@project-serum/serum";
import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { convert } from "@stepfinance/common";
import type { RaydiumLiquidityStateLayoutV4 } from "@stepfinance/liquidity";
import { RAYDIUM_LIQUIDITY_STATE_LAYOUT_V4 } from "@stepfinance/liquidity";
import BigNumber from "bignumber.js";
import { BN } from "bn.js";

import { HALF_WAD } from "../../constants";
import { getMultipleAccounts } from "../../contexts/accounts";
import type { MarketsContextState } from "../../contexts/market";
import type { LendingReserveLayout } from "../../models/token-lending/tulip/reserve";
import { LENDING_RESERVE_LAYOUT } from "../../models/token-lending/tulip/reserve";
import type { TulipReserve } from "../../models/token-lending/tulip/reserves";
import type { LendingObligationLayout, OrcaVaultLayout, VaultLayout } from "../../models/tulip";
import { ORCA_VAULT_LAYOUT, VAULT_LAYOUT } from "../../models/tulip";
import type { FarmObligationMap } from "../../models/tulip/leverageVaults";
import { cache } from "../cache";
import { getTokenByMint } from "../tokenInfo";

export const ETH_UNIT = new BigNumber((10 ** 18).toString());

export const findUserFarmAddress = async (
  authority: PublicKey,
  programId: PublicKey,
  userAddressIndex: BigNumber,
  farmIndex: BigNumber,
) => {
  /**
   * Create buffer arrays for index (always 0)
   * and _farm first byte with proper IDs, then fill with 0s
   */
  const indexBuffer = Buffer.alloc(8, userAddressIndex.toNumber());
  const farmBuffer = Buffer.alloc(8);
  //TODO find a better way for this
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  farmBuffer[0] = farmIndex;
  try {
    const seeds = [authority.toBuffer(), indexBuffer, farmBuffer];
    const k = await PublicKey.findProgramAddress(seeds, programId);
    return k;
  } catch (error) {}
};

export const findUserFarmObligationAddress = async (
  authority: PublicKey,
  userFarmAddr: PublicKey,
  programId: PublicKey,
  obligationIndex: BigNumber,
) => {
  try {
    const index = Buffer.alloc(8);
    //TODO find a better way for this
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    index[0] = obligationIndex;
    const seeds = [authority.toBuffer(), userFarmAddr.toBuffer(), index];
    return await PublicKey.findProgramAddress(seeds, programId);
  } catch (error) {}
};

const getVaultDataMap = async (connection: Connection, vaultAddresses: string[], poolVersion: number) => {
  const vaultDataMap: {
    [rawVaultAddress: string]: RaydiumLiquidityStateLayoutV4 | VaultLayout | OrcaVaultLayout | OpenOrders;
  } = {};
  const { keys, array } = await getMultipleAccounts(connection, vaultAddresses, "confirmed");
  let hasDecodedLpVaultAccount = false;
  array.forEach((vaultAccount, index) => {
    const accountAddress = keys[index];
    if (!hasDecodedLpVaultAccount) {
      //this is dependant on the order of vaultAddresses. First index is lp vault account
      hasDecodedLpVaultAccount = true;
      if (poolVersion === 0) {
        vaultDataMap[accountAddress] = VAULT_LAYOUT.decode(vaultAccount.data);
      } else {
        vaultDataMap[accountAddress] = ORCA_VAULT_LAYOUT.decode(vaultAccount.data);
      }
    } else if (vaultAccount.data.length === RAYDIUM_LIQUIDITY_STATE_LAYOUT_V4.span) {
      vaultDataMap[accountAddress] = RAYDIUM_LIQUIDITY_STATE_LAYOUT_V4.decode(vaultAccount.data);
    } else {
      const openOrderLayout = OpenOrders.getLayout(new PublicKey(accountAddress));
      if (vaultAccount.data.length === openOrderLayout?.span) {
        vaultDataMap[accountAddress] = openOrderLayout.decode(vaultAccount.data);
      }
    }
  });
  return vaultDataMap;
};

export const getDepositedLpTokens = (vault: OrcaVaultLayout | VaultLayout, userVaultShares: BigNumber) => {
  return userVaultShares.multipliedBy(vault.total_vault_balance.toString()).div(vault.total_vlp_shares.toString());
};

export const getPoolStatus = async (
  connection: Connection,
  lpMintAddress: PublicKey,
  poolCoinTokenaccount: PublicKey,
  poolPcTokenaccount: PublicKey,
) => {
  try {
    const mintData = await cache.queryMint(connection, lpMintAddress);

    if (mintData === undefined) {
      return;
    }
    const totalSupply = new BigNumber(mintData.info.supply.toString());
    const supplyDecimals = mintData.info.decimals;
    const coinTokenAccount = await connection.getTokenAccountBalance(poolCoinTokenaccount);
    const coinBalance = new BigNumber(coinTokenAccount.value.amount);
    const coinDecimals = new BigNumber(10 ** coinTokenAccount.value.decimals);
    const pcTokenAccount = await connection.getTokenAccountBalance(poolPcTokenaccount);
    const pcBalance = new BigNumber(pcTokenAccount.value.amount);
    const pcDecimals = new BigNumber(10 ** pcTokenAccount.value.decimals);

    return {
      totalSupply,
      supplyDecimals,
      coinBalance,
      coinDecimals,
      pcBalance,
      pcDecimals,
    };
  } catch (err) {
    console.error("unable to get poolStatus for ", lpMintAddress);
  }
};

export const getVaultInfo = async (
  connection: Connection,
  farmObligation: FarmObligationMap[keyof FarmObligationMap],
  priceInUSD: MarketsContextState["priceInUSD"],
  decodedObligation: LendingObligationLayout,
  poolVersion: number,
  tulipReserves: TulipReserve[],
) => {
  const poolStatus = await getPoolStatus(
    connection,
    new PublicKey(farmObligation.raydium_lp_mint_address),
    new PublicKey(farmObligation.raydium_coin_token_account),
    new PublicKey(farmObligation.raydium_pc_token_account),
  );
  if (poolStatus === undefined) {
    return;
  }
  const { pcBalance, pcDecimals, coinBalance, coinDecimals, totalSupply, supplyDecimals } = poolStatus;
  let reserve0Bal: BigNumber;
  let reserve1Bal: BigNumber;

  const vaultAddresses: string[] = [farmObligation.vault_account];

  if (poolVersion === 0) {
    vaultAddresses.push(farmObligation.raydium_amm_id, farmObligation.raydium_amm_open_orders);
  }

  const vaultAddressMap = await getVaultDataMap(connection, vaultAddresses, poolVersion);
  const vaultShares = decodedObligation.vaultShares;
  const lpTokenVault = vaultAddressMap[farmObligation.vault_account] as VaultLayout | OrcaVaultLayout;
  const userLpTokens = getDepositedLpTokens(lpTokenVault, new BigNumber(vaultShares.toString()));

  if (userLpTokens.eq(0)) {
    return;
  }
  /**
   * If we calculate Raydium vaults, we also get AMM circulating supply;
   */

  if (poolVersion === 0) {
    /**
     * To get AMM ID and fetch circulating values.
     */

    const ammAccountData = vaultAddressMap[farmObligation.raydium_amm_id] as RaydiumLiquidityStateLayoutV4;
    if (!ammAccountData) {
      return;
    }
    const { baseNeedTakePnl, quoteNeedTakePnl } = ammAccountData;

    /**
     * Get and decode AMM Open Order values
     */
    const openOrders = vaultAddressMap[farmObligation.raydium_amm_open_orders] as OpenOrders;

    if (openOrders === undefined) {
      return;
    }

    reserve0Bal = coinBalance
      .plus(openOrders.baseTokenTotal.toString())
      .minus(baseNeedTakePnl.toString())
      .div(coinDecimals);
    reserve1Bal = pcBalance
      .plus(openOrders.quoteTokenTotal.toString())
      .minus(quoteNeedTakePnl.toString())
      .div(pcDecimals);
  } else {
    reserve0Bal = coinBalance.div(coinDecimals);
    reserve1Bal = pcBalance.div(pcDecimals);
  }

  /**
   * Pool TVL calculations based on reserves and reserves prices.
   */
  const reserve0 = findReserveTokenByMint(farmObligation.base_token_mint, tulipReserves);

  if (!reserve0) {
    return;
  }

  const reserve0Price = priceInUSD(farmObligation.base_token_mint) ?? 0;
  const reserve1 = findReserveTokenByMint(farmObligation.quote_token_mint, tulipReserves);
  const reserve1Price = priceInUSD(farmObligation.quote_token_mint) ?? 0;

  if (!reserve1) {
    return;
  }

  const reserveAccounts = await getMultipleAccounts(connection, [reserve0.account, reserve1.account], "confirmed");
  const reserveAccountsMap = reserveAccounts.array.reduce((map, acc, index) => {
    map[reserveAccounts.keys[index]] = acc;
    return map;
  }, {});

  const supplyDec = new BigNumber(10 ** supplyDecimals);
  const reserve0Liquidity = reserve0Bal.multipliedBy(reserve0Price);
  const reserve1Liquidity = reserve1Bal.multipliedBy(reserve1Price);
  const poolTVL = reserve0Liquidity.plus(reserve1Liquidity);
  const unitLpValue = poolTVL.div(totalSupply.div(supplyDec));
  const totalLPs = poolTVL.div(unitLpValue);
  const userLpRatio = userLpTokens.div(supplyDec).div(totalLPs);
  const virtualValue = userLpTokens.multipliedBy(unitLpValue).div(supplyDec);
  const borrow1 = new BigNumber(decodedObligation.obligationBorrowOne.borrowedAmountWads.toString());
  const borrow2 = new BigNumber(decodedObligation.obligationBorrowTwo.borrowedAmountWads.toString());
  const mintAData = { address: farmObligation.base_token_mint, borrowedAmount: 0, borrowValue: 0 };
  const mintBData = { address: farmObligation.quote_token_mint, borrowedAmount: 0, borrowValue: 0 };

  if (!borrow1.isZero() && !borrow2.isZero()) {
    const reserve = reserveAccountsMap[decodedObligation.obligationBorrowOne.borrowReserve.toBase58()];
    if (!reserve) {
      return;
    }
    const decodedReserve1 = LENDING_RESERVE_LAYOUT.decode(reserve.data);
    const obligationData1 = getObligationData(
      decodedObligation.obligationBorrowOne,
      decodedReserve1,
      priceInUSD,
      tulipReserves,
    );
    if (obligationData1 === undefined) {
      return;
    }
    const borrowed1 = obligationData1.borrowed;
    const borrow1Value = obligationData1.borrowValue;
    const reserve2 = reserveAccountsMap[decodedObligation.obligationBorrowTwo.borrowReserve.toBase58()];
    const decodedReserve2 = LENDING_RESERVE_LAYOUT.decode(reserve2.data);
    const obligationData2 = getObligationData(
      decodedObligation.obligationBorrowTwo,
      decodedReserve2,
      priceInUSD,
      tulipReserves,
    );
    if (obligationData2 === undefined) {
      return;
    }
    const borrowed2 = obligationData2.borrowed;
    const borrow2Value = obligationData2.borrowValue;

    mintAData.borrowedAmount = borrowed1;
    mintAData.borrowValue = borrow1Value;

    mintBData.borrowedAmount = borrowed2;
    mintBData.borrowValue = borrow2Value;
  } else if (!borrow1.isZero()) {
    const reserve = reserveAccountsMap[decodedObligation.obligationBorrowOne.borrowReserve.toBase58()];
    if (!reserve) {
      return;
    }
    const decodedReserve = LENDING_RESERVE_LAYOUT.decode(reserve.data);
    const obligationData = getObligationData(
      decodedObligation.obligationBorrowOne,
      decodedReserve,
      priceInUSD,
      tulipReserves,
    );
    if (obligationData === undefined || !obligationData.obligationToken) {
      return;
    }
    if (obligationData.obligationToken.address === mintAData.address) {
      mintAData.borrowedAmount = obligationData.borrowed;
      mintAData.borrowValue = obligationData.borrowValue;
    } else if (obligationData.obligationToken.address === mintBData.address) {
      mintBData.borrowedAmount = obligationData.borrowed;
      mintBData.borrowValue = obligationData.borrowValue;
    }
  }

  const borrowValue = mintAData.borrowValue + mintBData.borrowValue;
  const equityValue = virtualValue.minus(borrowValue);

  /**
   * User LpTokens * token USD value = virtual value
   * borrowed = obligationBorrowX.borrowedAmountWads
   * virtual value - borrowed  = value
   */

  return {
    mintAData,
    mintBData,
    virtualValue,
    positionAmountA: userLpRatio.multipliedBy(reserve0Bal).toNumber(),
    positionAmountB: userLpRatio.multipliedBy(reserve1Bal).toNumber(),
    unitLpValue,
    equityValue,
    debtValue: borrowValue,
  };
};

const getObligationData = (
  decodedObligationBorrow:
    | LendingObligationLayout["obligationBorrowOne"]
    | LendingObligationLayout["obligationBorrowTwo"],
  decodedReserve: LendingReserveLayout,
  priceInUSD: MarketsContextState["priceInUSD"],
  tulipReserves: TulipReserve[],
) => {
  const obligation = findReserveTokenByAccount(decodedObligationBorrow.borrowReserve.toBase58(), tulipReserves);

  if (obligation === undefined) {
    return;
  }

  const obligationToken = getTokenByMint(obligation.mintAddress);
  const reservePrice = priceInUSD(obligation.mintAddress);
  const obligationCumulativeBorrowRate = decodedObligationBorrow.cumulativeBorrowRateWads;
  const reserveCumulativeBorrowRate = decodedReserve.liquidity.cumulativeBorrowRate;
  let obligationBorrowAmount = 0;
  if (
    obligationCumulativeBorrowRate.gte(reserveCumulativeBorrowRate) ||
    decodedObligationBorrow.borrowedAmountWads.isZero()
  ) {
    obligationBorrowAmount = convert(
      decodedObligationBorrow.borrowedAmountWads.div(HALF_WAD),
      obligationToken?.decimals,
    );
  } else {
    obligationBorrowAmount = convert(
      new BN(
        decodedObligationBorrow.borrowedAmountWads
          .div(HALF_WAD)
          .mul(reserveCumulativeBorrowRate)
          .div(obligationCumulativeBorrowRate),
      ),
      obligationToken?.decimals,
    );
  }

  const borrowValue: number = obligationBorrowAmount * (reservePrice ?? 0);
  const borrowedAsset: string = obligation.name;
  return {
    obligation,
    borrowed: obligationBorrowAmount,
    borrowValue,
    borrowedAsset,
    obligationToken,
  };
};

const findReserveTokenByMint = (mint: string, reserves: TulipReserve[]) => {
  const reserve = reserves.find((reserve) => reserve.mintAddress === mint);
  return reserve;
};

const findReserveTokenByAccount = (account: string, reserves: TulipReserve[]) => {
  const reserve = reserves.find((reserve) => reserve.account === account);
  return reserve;
};
